import { z } from 'zod';

import {
  EMPLOYER_TYPES,
  CONTENT_TYPES,
  PRODUCT_CATEGORIES,
  EXPERIENCE_LEVELS,
  WORD_COUNT_RANGES,
  RESEARCH_LEVELS,
  WRITING_TONES,
  WRITER_EXPERIENCE_YEARS,
  WRITER_INDUSTRIES,
  WRITER_LANGUAGES,
  ALL_LANGUAGES,
  USERNAME_REGEX,
  USERNAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
} from '@/lib/constants';

export const signUpSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
    role: z.enum(['employer', 'writer'], {
      error: 'Please select how you want to use Write4Me',
    }),
    terms_accepted: z.literal(true, {
      error: 'You must accept the terms and conditions',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SignUpInput = z.infer<typeof signUpSchema>;

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const otpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'OTP must be exactly 6 digits'),
});

export type OtpInput = z.infer<typeof otpSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const roleSelectionSchema = z.object({
  role: z.enum(['employer', 'writer']),
});

export type RoleSelectionInput = z.infer<typeof roleSelectionSchema>;

// ---------------------------------------------------------------------------
// Username — shared by writer + employer onboarding
// ---------------------------------------------------------------------------

export const usernameSchema = z
  .string()
  .min(USERNAME_MIN_LENGTH, `Username must be at least ${USERNAME_MIN_LENGTH} characters`)
  .max(USERNAME_MAX_LENGTH, `Username must be at most ${USERNAME_MAX_LENGTH} characters`)
  .regex(USERNAME_REGEX, 'Letters, numbers, underscores, and hyphens only');

// LinkedIn URL — optional. If provided, must be a valid URL on a linkedin.com host.
const linkedinUrlSchema = z
  .string()
  .url('Must be a valid LinkedIn URL')
  .refine(
    (val) => /^https?:\/\/([a-z]+\.)?linkedin\.com\//i.test(val),
    'Must be a LinkedIn profile URL (linkedin.com)',
  )
  .optional()
  .or(z.literal(''));

const optionalUrl = z
  .string()
  .url('Must be a valid URL')
  .optional()
  .or(z.literal(''));

// Portfolio URLs — optional. Each entry (if non-empty) must be a valid URL.
const portfolioUrlsSchema = z
  .array(z.string().url('Must be a valid URL').or(z.literal('')))
  .optional()
  .default([]);

export const employerOnboardingSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  username: usernameSchema,
  company_name: z.string().optional(),
  employer_type: z.enum(EMPLOYER_TYPES, { error: 'Employer type is required' }),
  website_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  bio: z.string().max(1000, 'Description must be under 1000 characters').optional().or(z.literal('')),
  country: z.string().min(1, 'Country is required'),
});

export type EmployerOnboardingInput = z.infer<typeof employerOnboardingSchema>;

const BIRTHDAY_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

export const writerOnboardingSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  username: usernameSchema,
  birthday: z
    .string()
    .min(1, 'Birthday is required')
    .regex(BIRTHDAY_REGEX, 'Must be in mm/dd/yyyy format')
    .refine((val) => {
      const [month, day, year] = val.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      return date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year;
    }, 'Invalid date')
    .refine((val) => {
      const [month, day, year] = val.split('/').map(Number);
      const birth = new Date(year, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age >= 18;
    }, 'You must be at least 18 years old'),
  country: z.string().min(1, 'Country is required'),
  experience_years: z.enum(WRITER_EXPERIENCE_YEARS, { error: 'Years of experience is required' }),
  industries: z
    .array(z.enum(WRITER_INDUSTRIES))
    .min(1, 'At least one industry is required'),
  languages: z
    .array(z.enum(ALL_LANGUAGES))
    .min(1, 'Select at least one writing language'),
  bio: z
    .string()
    .min(1, 'Author bio is required')
    .refine(
      (val) => val.trim().split(/\s+/).length <= 80,
      'Bio must be 80 words or less',
    ),
  linkedin_url: linkedinUrlSchema,
  facebook_url: optionalUrl,
  instagram_url: optionalUrl,
  twitter_url: optionalUrl,
  portfolio_urls: portfolioUrlsSchema,
  profile_photo_url: z.string().optional().or(z.literal('')),
});

export type WriterOnboardingInput = z.infer<typeof writerOnboardingSchema>;

// ---------------------------------------------------------------------------
// Unified onboarding schema (discriminated union on role)
// ---------------------------------------------------------------------------

const employerBranch = z.object({
  role: z.literal('employer'),
  name: z.string().min(1, 'Name is required'),
  username: usernameSchema,
  company_name: z.string().optional(),
  employer_type: z.enum(EMPLOYER_TYPES, { error: 'Employer type is required' }),
  website_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  bio: z.string().max(1000, 'Description must be under 1000 characters').optional().or(z.literal('')),
  country: z.string().min(1, 'Country is required'),
});

const writerBranch = z.object({
  role: z.literal('writer'),
  name: z.string().min(1, 'Name is required'),
  username: usernameSchema,
  birthday: z
    .string()
    .min(1, 'Birthday is required')
    .regex(BIRTHDAY_REGEX, 'Must be in mm/dd/yyyy format')
    .refine((val) => {
      const [month, day, year] = val.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      return date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year;
    }, 'Invalid date')
    .refine((val) => {
      const [month, day, year] = val.split('/').map(Number);
      const birth = new Date(year, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age >= 18;
    }, 'You must be at least 18 years old'),
  country: z.string().min(1, 'Country is required'),
  experience_years: z.enum(WRITER_EXPERIENCE_YEARS, { error: 'Years of experience is required' }),
  industries: z
    .array(z.enum(WRITER_INDUSTRIES))
    .min(1, 'At least one industry is required'),
  languages: z
    .array(z.enum(ALL_LANGUAGES))
    .min(1, 'Select at least one writing language'),
  bio: z
    .string()
    .min(1, 'Author bio is required')
    .refine(
      (val) => val.trim().split(/\s+/).length <= 80,
      'Bio must be 80 words or less',
    ),
  linkedin_url: linkedinUrlSchema,
  facebook_url: optionalUrl,
  instagram_url: optionalUrl,
  twitter_url: optionalUrl,
  portfolio_urls: portfolioUrlsSchema,
  profile_photo_url: z.string().optional().or(z.literal('')),
});

export const onboardingSchema = z.discriminatedUnion('role', [employerBranch, writerBranch]);

export type OnboardingInput = z.infer<typeof onboardingSchema>;
export type EmployerOnboardingData = z.infer<typeof employerBranch>;
export type WriterOnboardingData = z.infer<typeof writerBranch>;
